import React, { useState, useEffect, useCallback } from 'react';
import {
  Box, Typography, Table, TableBody, TableCell, TableContainer, TableHead,
  TableRow, Paper, Button, IconButton, Alert, CircularProgress, LinearProgress
} from '@mui/material';
import {
  Backup as BackupIcon, RestorePage as RestoreIcon,
  Delete as DeleteIcon, Refresh as RefreshIcon
} from '@mui/icons-material';
import axios from 'axios';

const API = '/api';
const authHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem('token')}` });

const formatSize = (bytes) => {
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(2)} MB`;
};

const Backup = () => {
  const [backups, setBackups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [restoring, setRestoring] = useState(null);
  const [message, setMessage] = useState(null);

  const fetchBackups = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/backups`, { headers: authHeaders() });
      if (res.data.success) setBackups(res.data.data);
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur chargement des backups' });
    }
    setLoading(false);
  }, []);

  useEffect(() => { fetchBackups(); }, [fetchBackups]);

  const handleCreate = async () => {
    setCreating(true);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/backups/create`, {}, { headers: authHeaders() });
      setMessage({ type: 'success', text: res.data.message });
      fetchBackups();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Erreur création backup' });
    }
    setCreating(false);
  };

  const handleRestore = async (filename) => {
    if (!window.confirm(`Restaurer ${filename} ? Cela remplacera toutes les données actuelles.`)) return;
    setRestoring(filename);
    setMessage(null);
    try {
      const res = await axios.post(`${API}/backups/restore`, { filename }, { headers: authHeaders() });
      setMessage({ type: 'success', text: res.data.message });
      fetchBackups();
    } catch (err) {
      setMessage({ type: 'error', text: err.response?.data?.message || 'Erreur restauration' });
    }
    setRestoring(null);
  };

  const handleDelete = async (filename) => {
    if (!window.confirm(`Supprimer ${filename} ?`)) return;
    try {
      await axios.delete(`${API}/backups/${encodeURIComponent(filename)}`, { headers: authHeaders() });
      setMessage({ type: 'success', text: 'Backup supprimé' });
      fetchBackups();
    } catch (err) {
      setMessage({ type: 'error', text: 'Erreur suppression' });
    }
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box>
          <Typography variant="h4" fontWeight="bold" sx={{ color: '#1f2937' }}>Sauvegarde & Restauration</Typography>
          <Typography variant="body2" color="text.secondary">Backup et restauration de la base de données</Typography>
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <IconButton onClick={fetchBackups}><RefreshIcon /></IconButton>
          <Button variant="contained" startIcon={<BackupIcon />} onClick={handleCreate} disabled={creating}
            sx={{ bgcolor: '#0ea5e9', '&:hover': { bgcolor: '#0284c7' }, textTransform: 'none' }}>
            {creating ? 'Création...' : 'Créer un backup'}
          </Button>
        </Box>
      </Box>

      {creating && <LinearProgress sx={{ mb: 2 }} />}

      {message && <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>{message.text}</Alert>}

      <TableContainer component={Paper} sx={{ borderRadius: 2 }}>
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: '#f9fafb' }}>
              <TableCell sx={{ fontWeight: 600 }}>Fichier</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Taille</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
              <TableCell sx={{ fontWeight: 600 }}>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4 }}><CircularProgress size={24} /></TableCell></TableRow>
            ) : backups.length === 0 ? (
              <TableRow><TableCell colSpan={4} align="center" sx={{ py: 4, color: '#9ca3af' }}>
                Aucun backup. Cliquez sur "Créer un backup" pour commencer.
              </TableCell></TableRow>
            ) : backups.map((b, i) => (
              <TableRow key={i} hover>
                <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{b.name}</TableCell>
                <TableCell>{formatSize(b.size)}</TableCell>
                <TableCell>{new Date(b.createdAt).toLocaleString('fr-FR')}</TableCell>
                <TableCell>
                  <Button size="small" variant="outlined" color="warning" startIcon={<RestoreIcon />}
                    disabled={restoring === b.name} onClick={() => handleRestore(b.name)}
                    sx={{ mr: 1, textTransform: 'none' }}>
                    {restoring === b.name ? 'Restauration...' : 'Restaurer'}
                  </Button>
                  <IconButton size="small" color="error" onClick={() => handleDelete(b.name)}>
                    <DeleteIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Box>
  );
};

export default Backup;


